import AdminNewSidebar from "components/admin/AdminNewSidebar";
import React, { useEffect, useRef, useState } from "react";
import { Box, Container } from "@mui/material";
import { Header } from "components";
import { useTranslation } from "react-i18next";
import { Table, Button, Form, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import apis from "../../services";
import { useDispatch } from "react-redux";
import { setAdmin } from "redux/slices/adminSlice";
import useNavigate from "hooks/Navigate";
import { calculateWholeShareAllocation } from "utils/shareAllocation";
import LotAllocationPreview from "components/shareLots/LotAllocationPreview";
import LotManagementPanel from "components/shareLots/LotManagementPanel";

export default function ShareManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const [loading, setLoading] = useState(false);

  const [investmentData, setInvestmentData] = useState({
    investorWalletId: "",
    commissionWalletId: "",
    commissionPercentage: 5,
    amount: 0,
    investmentDate: new Date().toISOString().split('T')[0],
    totalShares: 0,
    amountInvested: 0,
    dividendRemainder: 0,
    storeId: "",
    error: "",
  });

  const [pricePerShare, setPricePerShare] = useState(0);
  const [lotQuote, setLotQuote] = useState(null);
  const idempotencyKeyRef = useRef(null);

  const fetchTotalRecord = async () => {
    try {
      const res = await apis.getTotalRecord();
      if (res?.data?.TOTAL_PRICE_PER_SHARE) {
        setPricePerShare(res.data.TOTAL_PRICE_PER_SHARE);
      }
    } catch (err) {
      toast.error("Failed to fetch share price info");
    }
  };

  useEffect(() => {
    fetchTotalRecord();
  }, []);

  const onChangeHandler = (e) => {
    idempotencyKeyRef.current = null;
    const { id, value } = e.currentTarget;
    let updatedData = { ...investmentData, [id]: value };

    if (id === "amount") {
      const amountValue = parseFloat(value || 0);
      const allocation = calculateWholeShareAllocation(amountValue, pricePerShare);
      updatedData.totalShares = allocation.wholeShares;
      updatedData.amountInvested = allocation.amountInvested;
      updatedData.dividendRemainder = allocation.dividendRemainder;
      updatedData.error = "";
    }

    setInvestmentData(updatedData);
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!investmentData.investorWalletId) {
      return toast.error("Investor Wallet ID is required");
    }

    if (
      investmentData.commissionWalletId &&
      !investmentData.storeId
    ) {
      return toast.error("Store ID is required when Commission Wallet ID is provided");
    }

    if (
      investmentData.commissionWalletId &&
      !investmentData.commissionPercentage
    ) {
      return toast.error("Please set commission percentage");
    }

    if (investmentData.error) {
      return toast.error(investmentData.error);
    }

    try {
      setLoader(true);

      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current =
          window.crypto?.randomUUID?.() ||
          `manual-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }

      const requestData = {
        investorWalletId: investmentData.investorWalletId,
        amount: parseFloat(investmentData.amount),
        totalShares: lotQuote?.shareQuantity ?? investmentData.totalShares,
        storeId: investmentData.storeId || null,
        idempotencyKey: idempotencyKeyRef.current,
        commission: investmentData.commissionWalletId
          ? {
              walletId: investmentData.commissionWalletId,
              percentage: parseFloat(investmentData.commissionPercentage),
            }
          : null,
      };

      const response = await apis.createManualInvestment(requestData);

      if (response.data?.status) {
        idempotencyKeyRef.current = null;
        toast.success("Investment added successfully");
        setInvestmentData({
          investorWalletId: "",
          commissionWalletId: "",
          commissionPercentage: 5,
          amount: 0,
          investmentDate: new Date().toISOString().split("T")[0],
          totalShares: 0,
          amountInvested: 0,
          dividendRemainder: 0,
          storeId: "",
          error: "",
        });
      } else {
        throw new Error(response.data?.message || "Failed to add investment");
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || e.message || "Failed to add investment");
    } finally {
      setLoader(false);
    }
  };

  return (
    <Box className="dashboard-main">
      <AdminNewSidebar />
      <div className="dashboard-section-main">
        <Box component="main" sx={{ flexGrow: 1, height: "100vh", overflow: "auto" }} className="custom-scroll">
          <Header />
          {loading ? (
            <div className="loader-container">
              <div className="loader" />
            </div>
          ) : (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }} className="mainContainerComp" style={{ paddingBottom: "1px" }}>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <h2
                  style={{
                    color: '#000',
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '36px',
                    letterSpacing: '2px',
                    textAlign: 'center',
                    margin: 0,
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  {t("buttons.share-management")}
                </h2>
              </div>

              <div
                className="p-4 mb-5"
                style={{
                  border: "1px solid #e0201c",
                  borderRadius: "30px",
                  position: "relative",
                  zIndex: "10",
                }}
              >
                <h4 className="mb-4">{t("buttons.add-new-investment")}</h4>
                <Form onSubmit={submitHandler}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("buttons.investor-id")}</Form.Label>
                        <Form.Control
                          type="text"
                          id="investorWalletId"
                          placeholder="Enter Investor Wallet ID"
                          value={investmentData.investorWalletId}
                          onChange={onChangeHandler}
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>{t("buttons.store-id")}</Form.Label>
                        <Form.Control
                          type="text"
                          id="storeId"
                          placeholder="Enter Store ID"
                          value={investmentData.storeId}
                          onChange={onChangeHandler}
                          required={!!investmentData.commissionWalletId}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>{t("buttons.invested-amount")}</Form.Label>
                        <Form.Control
                          type="number"
                          id="amount"
                          placeholder="Enter Amount"
                          value={investmentData.amount}
                          onChange={onChangeHandler}
                          min="0"
                          step="0.01"
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("buttons.total-shares")}</Form.Label>
                        <Form.Control
                          type="text"
                          value={lotQuote?.shareQuantity ?? investmentData.totalShares}
                          readOnly
                        />
                        <Form.Text muted>
                          {Number(investmentData.dividendRemainder || 0) > 0
                            ? `$${Number(investmentData.amountInvested || 0).toFixed(2)} MXN will be invested and $${Number(investmentData.dividendRemainder).toFixed(2)} MXN will be added to the investor's dividends.`
                            : `$${Number(investmentData.amountInvested || 0).toFixed(2)} MXN buys exactly ${investmentData.totalShares || 0} whole shares.`}
                        </Form.Text>
                        <LotAllocationPreview amount={investmentData.amount} onQuote={setLotQuote} />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>{t("buttons.commission-wallet-id")}</Form.Label>
                        <Form.Control
                          type="text"
                          id="commissionWalletId"
                          placeholder="Enter Referrer Wallet ID"
                          value={investmentData.commissionWalletId}
                          onChange={onChangeHandler}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>{t("buttons.commission-percentage")}</Form.Label>
                        <Form.Control
                          type="number"
                          id="commissionPercentage"
                          placeholder="Enter Commission %"
                          value={investmentData.commissionPercentage}
                          onChange={onChangeHandler}
                          min="0"
                          max="100"
                          disabled={!investmentData.commissionWalletId}
                        />
                        <Form.Text muted>
                          {t("buttons.calulated-desc")}
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={12} className="mt-3 d-flex justify-content-center">
                      <Button
                        variant="outline-danger"
                        type="submit"
                        disabled={loader}
                        style={{ width: "200px", margin: "0 auto" }}
                      >
                        {loader ? t("buttons.submitting") : t("buttons.submit")}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </div>
              {/* <LotManagementPanel /> */}
            </Container>
          )}
        </Box>
      </div>
    </Box>
  );
}
